import React, { useEffect, useRef, useState } from "react";
import "./conversations.scss";
import { Dropdown } from "primereact/dropdown";
import messageExclamation from "../../../assets/message-exclamation.svg";
import EmptyDataView from "../../nodataview";
import PropTypes from "prop-types";
import Message from "./Message";
import MessageInput from "./MessageInput";
import MessageSkeleton from "../../../Pages/Process/components/MessageSkeleton";
import FlaggedMessage from "../../../Pages/Process/components/FlaggedMessage";
import CommentMessage from "../../../Pages/Process/components/CommentMessage";
import { Divider } from "primereact/divider";
import { groupMessagesByDate } from "../../../utils/conversationUtils";
import DeleteModal from "./DeleteModal";
import DeleteCommentModal from "./DeleteCommentModal";
import { useSearchParams } from "react-router-dom";
import PayslipProcessingMessage from "~/Pages/Process/components/PayslipProcessingMessage";
import PayslipAwaitingReviewMessage from "~/Pages/Process/components/PayslipAwaitingReviewMessage";
import useMessages from "~/Pages/Process/actions/useMessages";
import SuccessNotification from "~/components/entities/address/SuccessNotification";

const Conversations = ({
  title,
  messages = [],
  fetchMessages,
  isMessageLoading,
  formatDate,
  deleteConversation,
  isDeleteLoading,
  setDeleteLoading,
  deleteModal,
  setDeleteModal,
  selectedMessage,
  setSelectedMessage,
  entitydata,
  relatedType,
  serviceData = {},
  requestdata = {},
  requestType = "",
  deleteComment,
  setDeleteComment,
  processdata = {},
}) => {
  const { hasMorePrevData, hasMoreNextData, isDataFetching } = useMessages({
    fetchMessages,
    relatedType,
    requestType,
    serviceData,
  });

  const [searchParams] = useSearchParams();
  const commentId = searchParams.get("commentId");
  const messageRefs = useRef({});
  const scrollContainerRef = useRef(null);
  const sortOptions = ["Oldest First", "Newest First"];
  const [sortBy, setSortBy] = useState(sortOptions[0]);
  const previousScrollHeight = useRef(0);
  const isLoadingPrev = useRef(false);
  const isLoadingNext = useRef(false);
  const hasInitiallyScrolled = useRef(false);

  const sortHandler = (option) => {
    setSortBy(option.target.value);
  };

  // Scroll to bottom on initial load
  useEffect(() => {
    if (
      !isMessageLoading &&
      messages.length > 0 &&
      scrollContainerRef.current
    ) {
      const container = scrollContainerRef.current;
      setTimeout(() => {
        container.scrollTop = container.scrollHeight;

        // Mark as initially scrolled after a delay
        setTimeout(() => {
          hasInitiallyScrolled.current = true;
        }, 500); // Wait 500ms before enabling scroll detection
      }, 100);
    }
  }, [isMessageLoading]);

  // Scroll position when loading PREVIOUS messages
  // Load more previous messages if content doesn't fill container
  useEffect(() => {
    if (
      !isMessageLoading &&
      messages.length > 0 &&
      hasMorePrevData &&
      !isDataFetching
    ) {
      const checkContentHeight = () => {
        const container = scrollContainerRef.current;
        if (container) {
          const hasScrollbar = container.scrollHeight > container.clientHeight;
          //Scroll logic keeping it for debugging
          // console.log("Content height check:", {
          //   scrollHeight: container.scrollHeight,
          //   clientHeight: container.clientHeight,
          //   hasScrollbar,
          //   messagesCount: messages.length
          // });

          if (!hasScrollbar && hasMorePrevData && !isDataFetching) {
            // console.log("No scrollbar detected - auto-fetching more previous messages");
            loadPreviousMessages();
          }
        }
      };

      setTimeout(checkContentHeight, 200);
    }
  }, [messages.length, isMessageLoading, hasMorePrevData, isDataFetching]);

  // Scroll to comment if commentId exists
  useEffect(() => {
    if (isMessageLoading) return;

    if (commentId && messageRefs.current[commentId]) {
      setTimeout(() => {
        messageRefs.current[commentId].scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);
    }
  }, [commentId, isMessageLoading, messages]);

  const groupedMessages = groupMessagesByDate(messages || []);

  const closeModalHanlder = () => {
    setDeleteModal(false);
    setDeleteLoading(false);
    setSelectedMessage(null);
    setDeleteComment(false);
  };

  const deleteHandler = () => {
    setDeleteLoading(true);
    deleteConversation(selectedMessage?.id);
  };

  // Load PREVIOUS messages (scroll UP)
  const loadPreviousMessages = async () => {
    if (isDataFetching || !hasMorePrevData) {
      return;
    }

    try {
      isLoadingPrev.current = true;

      if (scrollContainerRef.current) {
        previousScrollHeight.current = scrollContainerRef.current.scrollHeight;
      }

      await fetchMessages("FETCHPREVDATA");
    } catch (error) {
      isLoadingPrev.current = false;
    }
  };

  // Load NEXT messages (scroll DOWN)
  const loadNextMessages = async () => {
    if (isDataFetching || !hasMoreNextData) {
      return;
    }

    try {
      isLoadingNext.current = true;

      await fetchMessages("FETCHNEXTDATA");

      isLoadingNext.current = false;
    } catch (error) {
      isLoadingNext.current = false;
    }
  };

  // Handle scroll event
  const handleScroll = (e) => {
    const container = e.target;
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;

    const topThreshold = 100;
    const bottomThreshold = 100;

    const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);

    // scroll event keeping it for debugging
    //   console.log("📜 SCROLL EVENT:", {
    //   scrollTop: Math.round(scrollTop),
    //   scrollHeight: Math.round(scrollHeight),
    //   clientHeight: Math.round(clientHeight),
    //   distanceFromBottom: Math.round(distanceFromBottom),
    //   atTop: scrollTop < topThreshold,
    //   atBottom: distanceFromBottom < bottomThreshold,
    //   hasMorePrev: hasMorePrevData,
    //   hasMoreNext: hasMoreNextData,
    //   isDataFetching: isDataFetching
    // });

    // Scrolling UP - near TOP
    if (scrollTop < topThreshold && hasMorePrevData && !isDataFetching) {
      loadPreviousMessages();
    }

    // Scrolling DOWN - near BOTTOM
    else if (
      distanceFromBottom < bottomThreshold &&
      hasMoreNextData &&
      !isDataFetching
    ) {
      loadNextMessages();
    }
  };

  return (
    <div className="conversations-layout">
      <div className="conversations-container">
        <div className="title-container">
          {title && <h3 className="title">{title}</h3>}
          <div className="title-sort-section">
            <p className="sort-label">Sort By</p>
            <Dropdown
              value={sortBy}
              options={sortOptions}
              className="sort-options"
              onChange={sortHandler}
              dropdownIcon="pi pi-chevron-up"
            />
          </div>
        </div>

        <div
          className="content-container"
          id="conversation-scroll"
          ref={scrollContainerRef}
          onScroll={handleScroll}
          style={{
            height: "calc(100vh - 300px)",
            overflowY: "auto",
          }}
        >
          {isMessageLoading && (!messages || messages.length === 0) ? (
            <div className="loading-container">
              <MessageSkeleton
                nameWidth="8rem"
                dateWidth="4rem"
                messageWidth="10rem"
              />
              <MessageSkeleton
                nameWidth="10rem"
                dateWidth="6rem"
                messageWidth="20rem"
              />
              <MessageSkeleton
                nameWidth="6rem"
                dateWidth="3rem"
                messageWidth="30rem"
              />
              <MessageSkeleton
                nameWidth="12rem"
                dateWidth="4rem"
                messageWidth="30rem"
              />
            </div>
          ) : messages && messages.length > 0 ? (
            <div style={{ minHeight: "150%" }}>
              {isDataFetching && isLoadingPrev.current && (
                <div
                  style={{
                    padding: "20px",
                    textAlign: "center",
                    backgroundColor: "#f8f9fa",
                  }}
                >
                  <MessageSkeleton
                    nameWidth="8rem"
                    dateWidth="4rem"
                    messageWidth="10rem"
                  />
                  <p style={{ marginTop: "10px", color: "#666" }}>
                    ⬆️ Loading previous messages...
                  </p>
                </div>
              )}

              {!hasMorePrevData && messages.length > 4 && (
                <div style={{ padding: "15px", textAlign: "center" }}>
                  <p style={{ color: "#666", fontSize: "14px" }}>
                    🏁 No more previous messages
                  </p>
                </div>
              )}

              {groupedMessages.map((group, index) => (
                <div key={`group-${index}`}>
                  <Divider>
                    <p className="date-group">{group.dateGroup}</p>
                  </Divider>
                  {group.messages.map((message, msgIndex) => {
                    const messageKey =
                      message.id ||
                      message.messageData?.id ||
                      `msg-${index}-${msgIndex}`;

                    return (
                      <React.Fragment key={messageKey}>
                        {message.messageData &&
                          message.type !== "TASK_COMMENTS" &&
                          message.type !== "PAYSLIP_PROCESSING_MESSAGE" &&
                          message.type !== "PAYSLIP_MATCHING_RESULT_MESSAGE" &&
                          message.type !== "PAYSLIP_SUBMITTED_MESSAGE" && (
                            <Message
                              messageInfo={message.messageData}
                              formatDate={formatDate}
                              conversation={message}
                              deletedAt={message.deletedAt}
                              setDeleteModal={setDeleteModal}
                              setSelectedMessage={setSelectedMessage}
                            />
                          )}

                        {message.type === "TASK_COMMENTS" && (
                          <div
                            ref={(el) => {
                              if (message.messageData?.id) {
                                messageRefs.current[message.messageData.id] =
                                  el;
                              }
                            }}
                          >
                            <CommentMessage
                              messageInfo={message.messageData}
                              formatDate={formatDate}
                              deletedAt={message.deletedAt}
                              setDeleteModal={setDeleteComment}
                              setSelectedMessage={setSelectedMessage}
                              conversation={message}
                            />
                          </div>
                        )}

                        {(message.type === "PAYSLIP_PROCESSING_MESSAGE" ||
                          message.type === "PAYSLIP_SUBMITTED_MESSAGE") && (
                          <PayslipProcessingMessage
                            messageInfo={message.messageData}
                            formatDate={formatDate}
                          />
                        )}

                        {message.type === "PAYSLIP_MATCHING_RESULT_MESSAGE" && (
                          <div
                            ref={(el) => {
                              const payslipId =
                                message?.messageData?.payslipMessageData?.id;
                              if (payslipId) {
                                messageRefs.current[payslipId] = el;
                              }
                            }}
                          >
                            <PayslipAwaitingReviewMessage
                              messageInfo={message.messageData}
                              formatDate={formatDate}
                              fetchMessages={fetchMessages}
                            />
                          </div>
                        )}

                        {message.flagData && (
                          <FlaggedMessage
                            messageInfo={message.flagData}
                            formatDate={formatDate}
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              ))}

              {isDataFetching && isLoadingNext.current && (
                <div
                  style={{
                    padding: "20px",
                    textAlign: "center",
                    backgroundColor: "#f8f9fa",
                  }}
                >
                  <MessageSkeleton
                    nameWidth="8rem"
                    dateWidth="4rem"
                    messageWidth="10rem"
                  />
                  <p style={{ marginTop: "10px", color: "#666" }}>
                    ⬇️ Loading next messages...
                  </p>
                </div>
              )}

              {!hasMoreNextData && messages.length > 4 && (
                <div style={{ padding: "15px", textAlign: "center" }}>
                  <p style={{ color: "#666", fontSize: "14px" }}>
                    ✅ You're all caught up!
                  </p>
                </div>
              )}
            </div>
          ) : (
            groupedMessages.map((group, index) => (
              <div key={index}>
                <Divider>
                  <p className="date-group">{group.dateGroup}</p>
                </Divider>
                {group.messages.map((message) => (
                  <>
                    {message.messageData &&
                      message.type !== "TASK_COMMENTS" &&
                      message.type !== "PAYSLIP_PROCESSING_MESSAGE" &&
                      message.type !== "PAYSLIP_MATCHING_RESULT_MESSAGE" &&
                      message.type !== "PAYSLIP_SUBMITTED_MESSAGE" && (
                        <Message
                          messageInfo={message.messageData}
                          formatDate={formatDate}
                          conversation={message}
                          deletedAt={message.deletedAt}
                          setDeleteModal={setDeleteModal}
                          setSelectedMessage={setSelectedMessage}
                          relatedType={relatedType}
                        />
                      )}
                    {message.type === "TASK_COMMENTS" && (
                      <div
                        ref={(el) =>
                          (messageRefs.current[message.messageData.id] = el)
                        }
                      >
                        <CommentMessage
                          messageInfo={message.messageData}
                          formatDate={formatDate}
                          deletedAt={message.deletedAt}
                          setDeleteModal={setDeleteComment}
                          setSelectedMessage={setSelectedMessage}
                          conversation={message}
                          relatedType={relatedType}
                        />
                      </div>
                    )}
                    {(message.type === "PAYSLIP_PROCESSING_MESSAGE" ||
                      message.type === "PAYSLIP_SUBMITTED_MESSAGE") && (
                      <PayslipProcessingMessage
                        messageInfo={message.messageData}
                        formatDate={formatDate}
                      />
                    )}
                    {message.type === "PAYSLIP_MATCHING_RESULT_MESSAGE" && (
                      <div
                        ref={(el) =>
                          (messageRefs.current[
                            message?.messageData?.payslipMessageData?.id
                          ] = el)
                        }
                      >
                        <PayslipAwaitingReviewMessage
                          messageInfo={message.messageData}
                          formatDate={formatDate}
                          fetchMessages={fetchMessages}
                        />
                      </div>
                    )}
                    {message.flagData && (
                      <FlaggedMessage
                        messageInfo={message.flagData}
                        formatDate={formatDate}
                      />
                    )}
                  </>
                ))}
              </div>
            ))
          )}
          {messages?.length === 0 && (
            <div className="empty-state">
              <EmptyDataView
                Icon={
                  <img
                    src={messageExclamation}
                    alt="empty-messages"
                    width={112}
                    height={112}
                  />
                }
                Header="Nothing here yet"
                Description="Add a comment, upload a file, or wait for others."
              />
            </div>
          )}
        </div>
      </div>

      <MessageInput
        fetchMessages={fetchMessages}
        entitydata={entitydata}
        relatedType={relatedType}
        serviceData={serviceData}
        requestdata={requestdata}
        requestType={requestType}
        processdata={processdata}
      />

      <DeleteModal
        isDeleteLoading={isDeleteLoading}
        deleteMessage={deleteHandler}
        visible={deleteModal}
        closeModal={closeModalHanlder}
      />

      {deleteComment && (
        <DeleteCommentModal
          isDeleteLoading={isDeleteLoading}
          deleteMessage={deleteHandler}
          visible={deleteComment}
          closeModal={closeModalHanlder}
        />
      )}
    </div>
  );
};

Conversations.propTypes = {
  title: PropTypes.string,
  messages: PropTypes.array,
  fetchMessages: PropTypes.func,
  isMessageLoading: PropTypes.bool,
  formatDate: PropTypes.func,
  deleteConversation: PropTypes.func,
  isDeleteLoading: PropTypes.bool,
  setDeleteLoading: PropTypes.func,
  deleteModal: PropTypes.bool,
  setDeleteModal: PropTypes.func,
  selectedMessage: PropTypes.object,
  setSelectedMessage: PropTypes.func,
  relatedType: PropTypes.string,
  serviceData: PropTypes.object,
  requestType: PropTypes.string,
  deleteComment: PropTypes.bool,
  setDeleteComment: PropTypes.func,
  entitydata: PropTypes.object,
  requestdata: PropTypes.object,
  processdata: PropTypes.object,
};

export default Conversations;
